import React, { useState, useRef } from "react";
import { Alert } from "@mui/material";
import Papa from "papaparse";
import request from "../utils/request";
const REQUIRED_COLUMNS = [//Sik
    "Entry Instructions",
    "Address Line 1",
    "City",
    "State",
    "Zip Code",
    "Check In",
    "Check Out",
    "Listing Title"
];
const BulkUploadModal = ({ onClose }) => {
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isParsed, setIsParsed] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);


    const fileInputRef = useRef();
    const handleDownloadTemplate = () => {
        const link = document.createElement("a");
        link.href = "/PropertyTemplate.csv";
        link.download = "PropertyTemplate.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleFileChange = (e) => {
        const uploadedFile = e.target.files[0];
        setFile(uploadedFile);
        setError("");
        setSuccess("");
        const reader = new FileReader();
        const extension = uploadedFile.name.split(".").pop();
        reader.onload = (event) => {
            try {
                let data;
                if (extension === "csv") {
                    const parsed = Papa.parse(event.target.result, {
                        header: true,
                        skipEmptyLines: true,
                    });
                    data = parsed.data;
                } else {
                    throw new Error("Unsupported file format. Only .csv files are allowed.");
                }
                const headers = Object.keys(data[0] || {});
                const missingColumns = REQUIRED_COLUMNS.filter(
                    (col) => !headers.includes(col)
                );
                if (missingColumns.length) {
                    setParsedData([]);
                    setIsParsed(false);
                    return setError(`Missing required columns: ${missingColumns.join(", ")}`);
                }
                if (!data.length) {
                    setParsedData([]);
                    setIsParsed(false);
                    return setError("The file is empty or does not contain valid data rows.");
                }
                setParsedData(data.slice(0, 5));
                setIsParsed(true);
                setError("");
            } catch (err) {
                setParsedData([]);
                setIsParsed(false);
                setError("Failed to parse file. Please ensure it’s formatted correctly.");
            }
        };
        reader.readAsText(uploadedFile);
    };
    const handleFileUpload = async () => {
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await request.post("/properties/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            if (res.data.success) {
                setSuccess("Properties uploaded successfully.");
                setUploadSuccess(true);
                setError("");
            } else {
                setError("Something went wrong. Please try again.");
                setSuccess("");
            }
        } catch (err) {
            setError("Upload failed. Please check your file format and try again.");
            setSuccess("");
        }
        finally {
            setUploadDone(true);
        }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white w-[95%] max-w-2xl rounded-xl p-6 shadow-lg relative">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-4 text-gray-500 hover:text-black text-md"
                >
                    ✕
                </button>
                {/* Step 1 */}
                {step === 0 && (
                    <>
                        <h2 className="text-2xl font-bold mb-2">Step 1: Download Template</h2>
                        <p className="text-gray-700 mb-4">
                            Download our CSV template and fill in your property information.
                            <br />
                            <span className="font-medium text-red-600">
                                Please do not modify the column headers.
                            </span>
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-700 mb-6 space-y-1">
                            <li>Follow the <strong>time format</strong> exactly as shown (e.g. 4:00 PM).</li>
                            <li><strong>Delete the example row</strong> before uploading.</li>
                            <li>Save the file as a <strong>.CSV</strong> format <strong>ONLY</strong>.</li>
                            <li><strong>Do not include table names</strong> or extra rows at the top.</li>
                        </ul>
                        <button
                            onClick={handleDownloadTemplate}
                            className="bg-black text-white px-4 py-2 rounded mb-6"
                        >
                            Download Property Template
                        </button>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setStep(1)}
                                className="bg-primary text-white px-4 py-2 rounded hover:bg-red-600"
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
                {/* Step 2 */}
                {step === 1 && (
                    <>
                        <h2 className="text-2xl font-bold mb-2">Step 2: Upload Your File</h2>
                        <p className="text-gray-700 mb-4">
                            Upload your completed CSV file.
                            <br />
                            <span className="text-sm text-gray-500">
                                If you run into any issues, please reach out to <strong>help@bellhopt.com</strong>.
                            </span>
                        </p>
                        <div
                            onDrop={(e) => {
                                e.preventDefault();
                                setFile(e.dataTransfer.files[0]);
                                handleFileChange({ target: { files: e.dataTransfer.files } });
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            className="mb-4 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
                            onClick={() => fileInputRef.current.click()}
                        >
                            <p className="text-gray-600 text-sm">
                                Drag & drop your file here, or <span className="text-primary font-medium">click to browse</span>
                            </p>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <button
                            onClick={handleFileUpload}
                            className={`
    bg-black text-white px-4 py-2 rounded mb-6 block mx-auto
    ${parsedData.length > 0 ? "ring-2 ring-blue-500 animate-pulse" : ""}
  `}
                        >
                            Upload File
                        </button>

                        {error && <Alert severity="error" className="mb-4">{String(error)}</Alert>}
                        {success && <Alert severity="success" className="mb-4">{String(success)}</Alert>}
                        {parsedData.length > 0 && (
                            <div className="overflow-auto max-h-64 border rounded p-2 text-sm mb-4">
                                <strong className="block mb-2 text-gray-800">Preview (first 5 rows):</strong>
                                <table className="min-w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr>
                                            {Object.keys(parsedData[0]).map((col) => (
                                                <th key={col} className="border px-2 py-1 font-semibold">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedData.map((row, idx) => (
                                            <tr key={idx}>
                                                {Object.values(row).map((val, i) => (
                                                    <td key={i} className="border px-2 py-1">{val}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <button
                                onClick={() => setStep(0)}
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => {
                                    if (uploadSuccess) {
                                        window.location.reload();
                                    } else {
                                        onClose();
                                    }
                                }}
                                disabled={!uploadDone}
                                className={`bg-primary text-white px-4 py-2 rounded text-sm hover:bg-red-600 ${!uploadDone ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                Finish
                            </button>

                        </div>
                    </>
                )}
                {/* Step indicator */}
                <div className="absolute bottom-1 right-1 text-sm text-gray-500">
                    {step + 1}/2
                </div>
            </div>
        </div>
    );
};
export default BulkUploadModal;

